import * as z from 'zod';
import * as io from 'io-ts';
import { Either, left, right } from 'fp-ts/lib/Either';
import { PathReporter } from 'io-ts/lib/PathReporter';

export type Validator<T> = io.Type<T> | z.ZodType<T>;
export type Validation<T> = Either<string, T>;

export const isIoType = <T>(d: Validator<T>): d is io.Type<T> =>
    d instanceof io.Type;

const validateIO =
    <T>(validator: io.Type<T>) =>
    (u: unknown): Validation<T> => {
        const result = validator.decode(u);
        return result.isLeft()
            ? left(PathReporter.report(validator.decode(u)).join('\n'))
            : right(result.value);
    };

const validateZod =
    <T>(validator: z.ZodType<T>) =>
    (u: unknown): Validation<T> => {
        const result = validator.safeParse(u);
        return !result.success
            ? left(String(result.error))
            : right(result.data as T);
    };

export const validate = <T>(
    validator: Validator<T>,
    u: unknown,
): Validation<T> =>
    isIoType(validator) ? validateIO(validator)(u) : validateZod(validator)(u);

const issueToError =
    (value: unknown, context: io.Context) =>
    (issue: z.core.$ZodIssue): io.ValidationError => {
        const ioPath = context.map(e => e.key).filter(k => k !== '');
        const path = ioPath.concat(issue.path.map(String)).join('/');
        return { value, context, message: `${path}: ${issue.message}` };
    };

export const ioFromZod = <T>(schema: z.ZodType<T>, name: string): io.Type<T> =>
    new io.Type<T>(
        name,
        (u): u is T => schema.safeParse(u).success,
        (u, c) => {
            const result = schema.safeParse(u);
            return result.success
                ? io.success(result.data)
                : io.failures(result.error.issues.map(issueToError(u, c)));
        },
        io.identity,
    );
